import { textoDoRelatorio } from '@sheafyield/dominio';
import { CONTA_LOCAL, type Banco } from '@sheafyield/sync';
import { useEffect, useRef, useState } from 'react';

import { banco, cliente, motor } from '../app/contexto.js';
import { useSessao } from '../app/hooks.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { sessaoAberta } from '../dados/consultas.js';
import { useDiaLocal, useViradaDeMinuto } from '../hoje/relogio.js';
import { salvarParaIdentidade } from '../ajustes/PerfilEMeta.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { Botao } from '../ds/Botao.js';
import { Toast } from '../ds/Toast.js';
import { FolhaEditarMes } from './FolhaEditarMes.js';
import { CompartilharRelatorio } from './CompartilharRelatorio.js';
import { editarMes, retomarAutomatico, marcarEnviado } from './dados.js';
import { Historico } from './Historico.js';
import { lerRelatorio, type VisaoDoRelatorio } from './dados.js';
import { ResumoDoMes } from './ResumoDoMes.js';
import './relatorio.css';

export interface DepsDoRelatorio {
  banco: Banco;
  motor?: MotorObservavel;
  dono: string | undefined;
  ler: (
    banco: Banco,
    dono: string,
    ano: number,
    mes: number,
    agora: Date,
  ) => Promise<VisaoDoRelatorio>;
  donoAtual?: DonoDasLinhas;
  comandos?: {
    editar: typeof editarMes;
    retomar: typeof retomarAutomatico;
    enviar: typeof marcarEnviado;
  };
  lerSessaoAberta?: (banco: Banco) => ReturnType<typeof sessaoAberta>;
}

const comandosDoApp = {
  editar: editarMes,
  retomar: retomarAutomatico,
  enviar: marcarEnviado,
};
const donoDoApp = donoDaSessao(cliente);
type FolhaAberta =
  | { identidade: string; tipo: 'editar' }
  | { identidade: string; tipo: 'compartilhar'; texto: string };

export function RelatorioTelaCom({ deps }: { deps: DepsDoRelatorio }) {
  const dia = useDiaLocal();
  const agora = new Date();
  const [selecionado, setSelecionado] = useState({
    ano: agora.getFullYear(),
    mes: agora.getMonth() + 1,
  });
  const [folha, setFolha] = useState<FolhaAberta | null>(null);
  const [ultima, setUltima] = useState<{
    recorte: string;
    visao: VisaoDoRelatorio;
  } | null>(null);
  const [envio, setEnvio] = useState<{
    identidade: string;
    pendente: boolean;
    erro: string | null;
    aviso: string | null;
  } | null>(null);
  const envioEmCurso = useRef<string | null>(null);
  const [seguindoAtual, setSeguindoAtual] = useState(true);
  const aberta = useConsultaCom(
    deps.motor ?? motor,
    `sessao-aberta:${deps.dono ?? 'carregando'}`,
    async () =>
      deps.dono === undefined
        ? null
        : ((await (deps.lerSessaoAberta ?? sessaoAberta)(deps.banco)) ?? null),
  );
  const tique = useViradaDeMinuto(
    aberta.dados === undefined || aberta.dados === null
      ? null
      : new Date(aberta.dados.inicio).getTime(),
    'na virada',
  );
  const hojeDoCalendario = new Date(dia);
  const corrente = {
    ano: hojeDoCalendario.getFullYear(),
    mes: hojeDoCalendario.getMonth() + 1,
  };
  if (
    seguindoAtual &&
    (selecionado.ano !== corrente.ano || selecionado.mes !== corrente.mes)
  )
    setSelecionado(corrente);
  const recorte = `${deps.dono ?? 'carregando'}:${selecionado.ano}:${selecionado.mes}`;
  const consulta = useConsultaCom(
    deps.motor ?? motor,
    `relatorio:${deps.dono ?? 'carregando'}:${selecionado.ano}:${selecionado.mes}:${dia}:${tique}`,
    async () => {
      if (deps.dono === undefined) return undefined;
      const visao = await deps.ler(
        deps.banco,
        deps.dono,
        selecionado.ano,
        selecionado.mes,
        new Date(),
      );
      return { recorte, visao };
    },
  );
  // Tique não é troca de identidade. A resposta carrega seu recorte para
  // preservar folhas durante releituras sem mostrar dados de outro mês/dono.
  const lida = consulta.dados?.recorte === recorte ? consulta.dados : undefined;
  if (lida !== undefined && lida !== ultima) setUltima(lida);
  const visao =
    lida?.visao ?? (ultima?.recorte === recorte ? ultima.visao : undefined);
  const identidade = `${recorte}:${visao?.perfil.tipo ?? ''}:${visao?.perfil.meta_horas_mes ?? ''}`;
  const identidadeAtual = useRef(identidade);
  useEffect(() => {
    identidadeAtual.current = identidade;
  }, [identidade]);
  if (folha !== null && folha.identidade !== identidade) setFolha(null);
  if (consulta.erro && visao === undefined)
    return (
      <p role="alert">Não deu para ler o relatório: {consulta.erro.message}</p>
    );
  if (deps.dono === undefined || visao === undefined)
    return <p aria-busy="true">Carregando relatório…</p>;
  const donoExibido = deps.dono;
  const comandos = deps.comandos ?? comandosDoApp;
  const futuro =
    selecionado.ano > corrente.ano ||
    (selecionado.ano === corrente.ano && selecionado.mes > corrente.mes);
  const estadoDoEnvio = envio?.identidade === identidade ? envio : null;
  const donoGuardado: DonoDasLinhas = async () => {
    if (identidadeAtual.current !== identidade)
      throw new Error('O relatório mudou. Confira o mês antes de salvar.');
    return (deps.donoAtual ?? donoDoApp)();
  };
  async function atualizarVisao(): Promise<VisaoDoRelatorio> {
    return salvarParaIdentidade(donoExibido, donoGuardado, async (dono) => {
      await dono();
      const nova = await deps.ler(
        deps.banco,
        donoExibido,
        selecionado.ano,
        selecionado.mes,
        new Date(),
      );
      await dono();
      return nova;
    });
  }
  async function enviar() {
    if (
      envioEmCurso.current === identidade ||
      futuro ||
      !visao?.baseCompleta ||
      visao.atual.enviado_em
    )
      return;
    envioEmCurso.current = identidade;
    setEnvio({ identidade, pendente: true, erro: null, aviso: null });
    try {
      await salvarParaIdentidade(donoExibido, donoGuardado, (dono) =>
        comandos.enviar(
          deps.banco,
          dono,
          selecionado.ano,
          selecionado.mes,
          visao.revisao,
          new Date(),
        ),
      );
      if (identidadeAtual.current === identidade)
        setEnvio({
          identidade,
          pendente: false,
          erro: null,
          aviso: 'Relatório marcado como enviado.',
        });
    } catch (causa) {
      if (identidadeAtual.current === identidade)
        setEnvio({
          identidade,
          pendente: false,
          erro:
            causa instanceof Error
              ? causa.message
              : 'Não deu para marcar o envio. Tente novamente.',
          aviso: null,
        });
    } finally {
      if (envioEmCurso.current === identidade) envioEmCurso.current = null;
    }
  }
  return (
    <>
      {consulta.erro ? (
        <p role="alert">
          Não deu para atualizar o relatório: {consulta.erro.message}
        </p>
      ) : null}
      {lida === undefined ? <p role="status">Atualizando totais…</p> : null}
      <ResumoDoMes visao={visao} />
      {futuro ? (
        <p>Mês futuro: a marcação de envio ainda não está disponível.</p>
      ) : null}
      <div className="relatorio__acoes">
        <Botao
          variante="secundario"
          onClick={(evento) => {
            // Safari não foca botão ao tocar; a folha precisa de um alvo
            // concreto para devolver o foco quando fechar.
            evento.currentTarget.focus();
            setFolha({ identidade, tipo: 'editar' });
          }}
        >
          Editar totais
        </Botao>
        <Botao
          variante="secundario"
          onClick={(evento) => {
            evento.currentTarget.focus();
            setFolha({
              identidade,
              tipo: 'compartilhar',
              texto: textoDoRelatorio(visao.atual, visao.perfil),
            });
          }}
        >
          Compartilhar relatório
        </Botao>
        <Botao
          disabled={
            futuro ||
            !visao.baseCompleta ||
            Boolean(visao.atual.enviado_em) ||
            lida === undefined
          }
          carregando={estadoDoEnvio?.pendente ?? false}
          onClick={() => {
            void enviar();
          }}
        >
          Marcar como enviado
        </Botao>
      </div>
      {estadoDoEnvio?.erro ? <p role="alert">{estadoDoEnvio.erro}</p> : null}
      <Toast
        mensagem={estadoDoEnvio?.aviso ?? null}
        chave={estadoDoEnvio?.aviso ?? null}
      />
      <Historico
        meses={visao.meses}
        tipoDePerfil={visao.perfil.tipo}
        atual={corrente}
        selecionado={selecionado}
        aoSelecionar={(ano, mes) => {
          setSeguindoAtual(ano === corrente.ano && mes === corrente.mes);
          setSelecionado({ ano, mes });
        }}
      />
      {folha?.tipo === 'editar' && folha.identidade === identidade ? (
        <FolhaEditarMes
          key={identidade}
          identidade={identidade}
          visao={visao}
          aoFechar={() => setFolha(null)}
          aoAtualizar={atualizarVisao}
          aoSalvar={async (entrada, revisao) => {
            await salvarParaIdentidade(donoExibido, donoGuardado, (dono) =>
              comandos.editar(
                deps.banco,
                dono,
                selecionado.ano,
                selecionado.mes,
                entrada,
                revisao,
                new Date(),
              ),
            );
          }}
          aoRetomar={async (revisao) => {
            await salvarParaIdentidade(donoExibido, donoGuardado, (dono) =>
              comandos.retomar(
                deps.banco,
                dono,
                selecionado.ano,
                selecionado.mes,
                revisao,
                new Date(),
              ),
            );
          }}
        />
      ) : null}
      {folha?.tipo === 'compartilhar' && folha.identidade === identidade ? (
        <CompartilharRelatorio
          key={identidade}
          texto={folha.texto}
          aoFechar={() => setFolha(null)}
        />
      ) : null}
    </>
  );
}

export function RelatorioTela() {
  const sessao = useSessao();
  return (
    <RelatorioTelaCom
      deps={{
        banco,
        dono:
          sessao === undefined ? undefined : (sessao?.user.id ?? CONTA_LOCAL),
        ler: lerRelatorio,
      }}
    />
  );
}
